import React, { createContext, useContext, useEffect, useState } from "react";
import { api, setAuth, clearAuth, getUser } from "./api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getUser());
  const [loading, setLoading] = useState(true);

  // Sincronizza il flag localStorage in base al campo `has_passkey` restituito
  // dall'endpoint di login/me. Usato dal Login per mostrare il pulsante
  // "Accedi con Passkey" primario quando l'utente ha già almeno una passkey.
  const _syncPasskeyFlag = (userObj) => {
    try {
      if (userObj?.has_passkey) localStorage.setItem("ht_passkey_last_used", "1");
      else if (userObj && userObj.has_passkey === false) localStorage.removeItem("ht_passkey_last_used");
    } catch {}
  };

  useEffect(() => {
    // Tenta la /me: se c'è un cookie httpOnly valido OPPURE un token in localStorage,
    // l'axios instance (withCredentials + interceptor) lo userà automaticamente.
    const hasLegacyToken = !!localStorage.getItem("ht_token");
    const hasStoredUser = !!localStorage.getItem("ht_user");
    if (!hasLegacyToken && !hasStoredUser) {
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then(r => { setUser(r.data); _syncPasskeyFlag(r.data); })
      .catch(() => { clearAuth(); setUser(null); })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    if (r.data.two_fa_required) {
      return { two_fa_required: true, interim_token: r.data.interim_token };
    }
    setAuth(r.data.access_token, r.data.user, r.data.refresh_token);
    setUser(r.data.user);
    _syncPasskeyFlag(r.data.user);
    return r.data.user;
  };

  const complete2FA = async (interim_token, code, is_backup = false) => {
    const r = await api.post("/auth/login/2fa", { interim_token, code, is_backup });
    setAuth(r.data.access_token, r.data.user, r.data.refresh_token);
    setUser(r.data.user);
    _syncPasskeyFlag(r.data.user);
    return r.data.user;
  };

  // Applica al context una sessione ottenuta via Passkey (WebAuthn).
  // `data` è la risposta di `/auth/webauthn/auth/verify`.
  const applyPasskeySession = (data) => {
    setAuth(data.access_token, data.user, data.refresh_token);
    setUser(data.user);
    try { localStorage.setItem("ht_passkey_last_used", "1"); } catch {}
    return data.user;
  };

  // Avvia il flow Google OAuth 2.0 (Authorization Code, ux_mode=redirect).
  // Reindirizza a accounts.google.com; Google torna a /auth/google/callback?code=...
  // che sarà gestito da GoogleCallback.jsx via completeGoogleCode().
  const googleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    if (!clientId) {
      // eslint-disable-next-line no-alert
      alert("Google OAuth non configurato (REACT_APP_GOOGLE_OAUTH_CLIENT_ID mancante).");
      return;
    }
    const state = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
    sessionStorage.setItem("ht_google_oauth_state", state);
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "online",
      prompt: "select_account",
      include_granted_scopes: "true",
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  // Chiamata da GoogleCallback.jsx dopo aver estratto ?code= dalla URL.
  // Ritorna:
  //   { needs_registration: true, google: {...}, pending_token } se l'utente è nuovo
  //   { user, ... } se l'utente esiste ed è attivo
  const completeGoogleCode = async (code, extra = {}) => {
    const r = await api.post("/auth/google/callback", { code, ...extra });
    if (r.data?.needs_registration) {
      return {
        needs_registration: true,
        google: r.data.google,
        pending_token: r.data.pending_token,
      };
    }
    if (r.data?.needs_invite_details) return r.data;
    setAuth(r.data.access_token, r.data.user, r.data.refresh_token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    // Best-effort revoca lato server. Il backend legge il refresh dal cookie
    // httpOnly; passiamo anche quello legacy da localStorage per retro-compat.
    const refresh = localStorage.getItem("ht_refresh");
    // Iter209dl — Unsubscribe web push su logout esplicito (non blocca).
    try {
      const { unsubscribeWebPush } = await import("@/lib/webPush");
      await unsubscribeWebPush();
    } catch (_) { /* no-op */ }
    try {
      await api.post("/auth/logout", { refresh_token: refresh || null });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[auth] logout revoke failed:", err?.message || err);
      }
    }
    // Reset del flag "web push già chiesto" così al prossimo login diverso account
    // il nuovo utente possa dare il consenso.
    try { localStorage.removeItem("ht.webPushAsked"); } catch (_) {}
    clearAuth();
    setUser(null);
  };

  // Iter140v: refresh dei dati utente correnti (chiamato dalla Dashboard e
  // ovunque serva riflettere modifiche admin (es. cambio plafond/fascia).
  const refreshUser = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      _syncPasskeyFlag(r.data);
      try { localStorage.setItem("ht_user", JSON.stringify(r.data)); } catch {}
      return r.data;
    } catch (err) {
      return null;
    }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, complete2FA, googleLogin, completeGoogleCode, logout, setUser, applyPasskeySession, refreshUser }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
